<template>
    <div>
        <form
            v-if="!showExternalMessage && !showRedirectionMessage"
            class="mt-8 space-y-6 pb-24 md:pb-0"
            @change="form.errors.clear($event.target.name)"
            @keyup="form.errors.clear($event.target.name)"
            @submit.prevent="submit"
            enctype="multipart/form-data"
        >
            <p class="form-error" v-if="form.errors.has('general')" v-text="form.errors.get('general')"></p>

            <div>
                <label for="name" class="label">
                    {{ $t('Full name') }}
                    <span class="text-sm text-red-700">*</span>
                </label>
                <input type="text" name="name" id="name" v-model="form.name" class="input" />
                <p class="form-error" v-if="form.errors.has('name')" v-text="form.errors.get('name')"></p>
            </div>

            <div>
                <label for="email" class="label">
                    {{ $t('Email address') }}
                    <span class="text-sm text-red-700">*</span>
                </label>
                <input type="email" name="email" id="email" v-model="form.email" class="input" />
                <p class="form-error" v-if="form.errors.has('email')" v-text="form.errors.get('email')"></p>
            </div>

            <div>
                <label for="phone_number" class="label">{{ $t('Phone number') }}</label>
                <input type="tel" name="phone_number" id="phone_number" v-model="form.phone_number" class="input" />
                <p class="form-error" v-if="form.errors.has('phone_number')" v-text="form.errors.get('phone_number')"></p>
            </div>

            <div v-if="props.offer?.ask_for_canadian_status">
                <label for="canadian_status" class="label">
                    {{ $t('What is your Canadian status?') }}
                </label>
                <select name="canadian_status" id="canadian_status" v-model="form.canadian_status" class="input">
                    <option value="">{{ $t('Select an option') }}</option>
                    <option value="citizen">{{ $t('Canadian citizen') }}</option>
                    <option value="permanent_resident">{{ $t('Permanent resident') }}</option>
                    <option value="open_work_permit">{{ $t('Open work permit') }}</option>
                    <option value="closed_work_permit">{{ $t('Closed work permit') }}</option>
                    <option value="other">{{ $t('Other status') }}</option>
                    <option value="prefer_not_to_say">{{ $t('Prefer not to say') }}</option>
                </select>
                <p class="form-error" v-if="form.errors.has('canadian_status')" v-text="form.errors.get('canadian_status')"></p>
            </div>

            <template v-if="!isExternal && offer.custom_form_fields">
                <CustomFormField
                    v-for="field in offer.custom_form_fields"
                    :key="field.internal_id"
                    :field="field"
                    v-model="form[field.internal_id]"
                />
            </template>

            <div>
                <label for="resume" class="label">
                    {{ $t('Resume') }}
                    <span class="text-sm text-red-700">*</span>
                </label>
                <FileInput name="resume" v-model="form.resume" @update="form.errors.clear('resume')" />
                <p class="form-error" v-if="form.errors.has('resume')" v-text="form.errors.get('resume')"></p>
            </div>

            <div>
                <label class="flex items-center space-x-4">
                    <Toggle v-model="form.newsletter" />
                    <span>
                        {{ $t('I wish to subscribe to the newsletter and SMS notifications from CAMAQ and its employability partners.') }}
                    </span>
                </label>
            </div>

            <div>
                <Button
                    class="flex w-full items-center justify-center space-x-4 text-sm uppercase tracking-wide lg:p-6 lg:text-base"
                    :busy="form.processing"
                >
                    <span v-if="!isExternal">{{ $t('Submit the application') }}</span>
                    <span v-else>{{ $t('Continue application') }}</span>
                    <IconPlaneDeparture v-if="!isExternal" size="24" />
                    <IconArrowNarrowRight v-else size="24" />
                </Button>
            </div>
        </form>

        <div v-if="showExternalMessage" class="flex flex-col items-center space-y-8">
            <p class="text-sm font-medium text-emerald-900">
                {{ $t('You have successfully started the application process !') }}
            </p>

            <a :href="offer.application_url" target="_blank" rel="noopener noreferrer" class="button block p-4">
                {{ $t('Click here to continue the application process') }}
            </a>

            <p class="text-sm text-gray-600">
                {{ $t('You will be automatically redirected to the next step in :seconds', { seconds: countdown }) }}
            </p>
        </div>

        <div v-if="showRedirectionMessage" class="flex flex-col items-center space-y-8">
            <p class="text-sm font-medium text-emerald-900">
                {{ $t('You have successfully applied !') }}
            </p>

            <a :href="offer.redirection_url" target="_blank" rel="noopener noreferrer" class="button block p-4">
                {{ $t("Click here to visit the employer's website") }}
            </a>

            <p class="text-sm text-gray-600">
                {{ $t('You will be automatically redirected in :seconds', { seconds: countdown }) }}
            </p>
        </div>
    </div>
</template>

<script setup>
    import Form from '@/forms';
    import FileInput from '@/Shared/FileInput.vue';
    import Toggle from '@/Shared/Toggle';
    import { IconArrowNarrowRight, IconPlaneDeparture } from '@tabler/icons-vue';
    import Swal from 'sweetalert2';
    import { computed, onBeforeMount, reactive, ref } from 'vue';
    import CustomFormField from './CustomFormField';

    const emit = defineEmits(['success']);

    const props = defineProps({
        offer: Object,
    });

    const isExternal = computed(() => props.offer.application_type == 'external');
    const countdown = ref(5);

    const showExternalMessage = ref(false);
    const showRedirectionMessage = ref(false);

    let form = reactive(
        new Form({
            name: '',
            email: '',
            phone_number: '',
            canadian_status: '',
            resume: '',
            newsletter: false,
        })
    );

    onBeforeMount(() => {
        if (!isExternal.value && props.offer.custom_form_fields) {
            props.offer.custom_form_fields.forEach(field => {
                form.add(field.internal_id, '');
            });
        }
    });

    function countdownToRedirect(url, seconds = 5) {
        countdown.value = seconds;

        setInterval(() => {
            countdown.value = countdown.value - 1;
            if (countdown.value <= 0) {
                countdown.value = 0;
                window.location = url;
            }
        }, 1000);
    }

    function submit() {
        form.post(`/emplois/${props.offer.slug}/application`)
            .then(response => {
                if (isExternal.value) {
                    showExternalMessage.value = true;
                    countdownToRedirect(props.offer.application_url);
                } else {
                    if (props.offer.redirection_url?.length > 0) {
                        showRedirectionMessage.value = true;
                        countdownToRedirect(props.offer.redirection_url);
                    } else {
                        emit('success');
                    }
                }
            })
            .catch(error => {
                const status = error.response?.status;

                if (status === 422) {
                    return;
                }

                if (status === 419) {
                    // CSRF token error, page expired (need refresh)
                    Swal.fire({
                        title: 'Page expirée',
                        text: 'La page est expirée, veuillez réactualiser la page et réessayer.',
                        icon: 'error',
                    });
                } else if (!error.response) {
                    // Network error, timeout, or CORS issue
                    Swal.fire({
                        title: 'Erreur de connexion',
                        text: 'Impossible de contacter le serveur. Veuillez vérifier votre connexion internet et réessayer.',
                        icon: 'error',
                    });
                } else {
                    Swal.fire({
                        title: 'Erreur',
                        text: "Une erreur s'est produite lors de l'application, veuillez réessayer. Si le problème persiste, veuillez contacter le support à support@camaq.ca",
                        icon: 'error',
                    });
                }

                throw new Error(
                    `Erreur application CAMAQ (status: ${status ?? 'N/A'} - statusText: ${error.response?.statusText ?? error.message ?? 'Erreur inconnue'})`
                );
            });
    }

    defineExpose({ form });
</script>
